import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react'
import {
	Box,
	Typography,
	Drawer,
	useMediaQuery,
	useTheme
} from '@mui/material'
import AddSingleAppointment from './AddSingleAppointment'
import AppointmentsOverview from './AppointmentsOverview'
import RecurringAppointmentsOverview from './RecurringAppointmentsOverview'
import { AP_OCC_CAP, L, Icon, LavMobileContext } from './appointmentLavenderFields'
import MsgIcon, { AiBadge } from '../messaging/MsgIcon'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * AddAppointmentDialog - Container component for merchant appointment creation flow
 * 
 * Desktop: Side panel that pushes calendar left (like ServiceEditorPanel)
 * Mobile: Bottom drawer
 * 
 * @param {boolean} open - Whether dialog is open
 * @param {function} onClose - Callback to close dialog
 * @param {object} merchant - Merchant object
 * @param {function} onAppointmentCreated - Callback after successful booking
 * @param {object} prefilledDateTime - Prefilled date and time { date: 'YYYY-MM-DD', time: 'HH:mm' }
 * @param {object} prefilledClient - Prefilled client object
 * @param {object} prefilledAppointment - Prefilled appointment fields (from AI suggestion).
 *   Shape: { client?, date?, time?, pet?, service?, addOns?, employee? }.
 *   When supplied, this seeds the form on open and supersedes prefilledClient/prefilledDateTime
 *   for any fields it provides.
 * @param {object} aiContext - Present only when opened from the messaging AI flow.
 *   Shape: { subtitle?, note? }. Swaps the header for the "Create appointment (AI)"
 *   variant and shows the AI note above the form. Other callers are unaffected.
 * @param {'panel'|'drawer'} desktopVariant - How the desktop surface is drawn.
 *   'panel' (default) is the fixed-width card that sits beside the calendar.
 *   'drawer' fills its container edge to edge, for callers that supply their own
 *   slide-over shell (see the Client Details booking drawer).
 */
const AddAppointmentDialog = forwardRef(function AddAppointmentDialog({
	open,
	onClose,
	merchant,
	onAppointmentCreated,
	prefilledDateTime = null,
	prefilledClient = null,
	prefilledAppointment = null,
	aiContext = null,
	desktopVariant = 'panel'
}, ref) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// Ref to hold save function from child components
	const saveActionRef = useRef(null)

	// Pull initial seeds: prefilledAppointment (AI) wins over individual prefilled* props.
	const initialClient = prefilledAppointment?.client ?? prefilledClient
	const initialDate =
		prefilledAppointment?.date ?? prefilledDateTime?.date ?? dayjs().format('YYYY-MM-DD')
	const initialTime = prefilledAppointment?.time ?? prefilledDateTime?.time ?? ''

	// State management
	const [currentStep, setCurrentStep] = useState('single') // 'single' | 'overview' | 'recurring'
	const currentStepRef = useRef('single')
	useEffect(() => { currentStepRef.current = currentStep }, [currentStep])
	const [selectedClient, setSelectedClient] = useState(initialClient)
	const [selectedDate, setSelectedDate] = useState(initialDate)
	const [selectedTime, setSelectedTime] = useState(initialTime)
	const [appointments, setAppointments] = useState([])
	const [editingIndex, setEditingIndex] = useState(null)

	// Recurring flow state.
	//   recurringBase       → the base appointment payload (incl. inline repeat config)
	//   recurringOccurrences → the generated [{date, time}] list passed to the overview
	const [recurringBase, setRecurringBase] = useState(null)
	const [recurringOccurrences, setRecurringOccurrences] = useState([])

	// Mobile "Booked!" confirmation (design: Mobile · key states → Booked).
	// On mobile a successful booking shows this screen instead of closing;
	// onAppointmentCreated is deferred until dismissal so parents that refresh
	// (or close) on creation don't kill the confirmation. pendingCreatedRef
	// stays true across "Add another" so the parent is always notified once.
	const [bookedInfo, setBookedInfo] = useState(null) // { names, dateLabel, timeLabel, recurring } | null
	const pendingCreatedRef = useRef(false)

	// Track previous open state so the reset only fires on fresh open,
	// not when prefilledDateTime changes while the dialog is already open.
	const wasOpen = useRef(false)
	useEffect(() => {
		if (open && !wasOpen.current) {
			setCurrentStep('single')
			setSelectedClient(prefilledAppointment?.client ?? prefilledClient)
			setSelectedDate(
				prefilledAppointment?.date ?? prefilledDateTime?.date ?? dayjs().format('YYYY-MM-DD')
			)
			setSelectedTime(prefilledAppointment?.time ?? prefilledDateTime?.time ?? '')
			setAppointments([])
			setEditingIndex(null)
			setRecurringBase(null)
			setRecurringOccurrences([])
			setBookedInfo(null)
			pendingCreatedRef.current = false
		}
		wasOpen.current = open
	}, [open, prefilledDateTime, prefilledClient, prefilledAppointment])

	// Recurring toggled on + Book in the OVERVIEW step → materialize the
	// occurrences from the repeat config (just dates + times, no DB writes yet)
	// and switch to the series-check step. `appointmentData` carries the whole
	// visit, so every occurrence can rebuild the same multi-pet booking.
	//
	// We also lift client/date/time into dialog state here so they survive a
	// later "Back" from the recurring step.
	const handleSetupRecurring = useCallback((appointmentData) => {
		setRecurringBase(appointmentData)
		if (appointmentData?.client) setSelectedClient(appointmentData.client)
		if (appointmentData?.date) setSelectedDate(appointmentData.date)
		if (appointmentData?.time) setSelectedTime(appointmentData.time)

		const tz = merchant?.timezone || 'UTC'
		const repeat = appointmentData?.repeat
		const base = dayjs.tz(`${appointmentData.date} ${appointmentData.time}`, tz)
		const occs = [{ date: base.format('YYYY-MM-DD'), time: base.format('HH:mm') }]

		if (repeat?.on) {
			// 'count' ends after N visits; 'date' generates up to the cap and
			// stops once past the chosen end date. Either way the series is hard-
			// capped at AP_OCC_CAP appointments total.
			const endDate = repeat.ends === 'date' && repeat.endDate
				? dayjs.tz(repeat.endDate, tz)
				: null
			const target = repeat.ends === 'count'
				? Math.min(AP_OCC_CAP, Math.max(1, parseInt(repeat.count, 10) || 1))
				: AP_OCC_CAP
			let i = 1
			while (occs.length < target) {
				const step = repeat.freq === 'monthly'
					? base.add(i, 'month')
					: base.add(7 * (repeat.freq === 'custom'
						? Math.max(1, parseInt(repeat.customWeeks, 10) || 1)
						: repeat.freq) * i, 'day')
				if (endDate && step.isAfter(endDate, 'day')) break
				occs.push({ date: step.format('YYYY-MM-DD'), time: step.format('HH:mm') })
				i++
			}
		}

		setRecurringOccurrences(occs)
		setCurrentStep('recurring')
	}, [merchant?.timezone])

	const handleRecurringBack = useCallback(() => {
		// The series is always set up from the overview, so Back returns there.
		// The visit itself lives in dialog state, so it rehydrates unchanged.
		setCurrentStep('overview')
	}, [])

	// Expose a method so CalendarView can update start date/time without resetting
	// the rest of the dialog state (appointments, client, step, etc.)
	useImperativeHandle(ref, () => ({
		updateStartDateTime(date, time) {
			setSelectedDate(date)
			setSelectedTime(time)
		}
	}))

	// Handle save from AddSingleAppointment
	const handleSingleSave = useCallback((appointmentData) => {
		if (editingIndex !== null) {
			// Editing existing appointment
			setAppointments(prev => {
				const updated = prev.map((apt, i) => (
					// The visit shares one employee: changing it on the first pet
					// re-assigns every pet in the group.
					editingIndex === 0 && i !== 0
						? { ...apt, employee: appointmentData.employee }
						: apt
				))
				updated[editingIndex] = {
					...updated[editingIndex],
					pet: appointmentData.pet,
					service: appointmentData.service,
					addOns: appointmentData.addOns,
					employee: appointmentData.employee
				}
				return updated
			})
			setEditingIndex(null)
		} else {
			// Adding new appointment
			if (appointments.length === 0) {
				// First appointment - save client, date, time
				setSelectedClient(appointmentData.client)
				setSelectedDate(appointmentData.date)
				setSelectedTime(appointmentData.time)
			}
			setAppointments(prev => [...prev, {
				pet: appointmentData.pet,
				service: appointmentData.service,
				addOns: appointmentData.addOns,
				employee: appointmentData.employee
			}])
		}
		setCurrentStep('overview')
	}, [appointments.length, editingIndex])

	// Handle add another pet
	const handleAddPet = useCallback(() => {
		setEditingIndex(null)
		setCurrentStep('single')
	}, [])

	// Handle edit appointment
	const handleEditAppointment = useCallback((index) => {
		setEditingIndex(index)
		setCurrentStep('single')
	}, [])

	// Handle delete appointment
	const handleDeleteAppointment = useCallback((index) => {
		setAppointments(prev => prev.filter((_, i) => i !== index))
	}, [])

	// Handle appointments change (e.g., duration adjustment)
	const handleAppointmentsChange = useCallback((updatedAppointments) => {
		setAppointments(updatedAppointments)
	}, [])

	// Handle cancel from single step
	const handleSingleCancel = useCallback(() => {
		if (appointments.length > 0) {
			// Go back to overview
			setEditingIndex(null)
			setCurrentStep('overview')
		} else {
			// Close dialog — flush a deferred created callback first (a booking
			// may have happened earlier in this session via "Add another").
			if (pendingCreatedRef.current) {
				pendingCreatedRef.current = false
				onAppointmentCreated?.()
			}
			onClose()
		}
	}, [appointments.length, onAppointmentCreated, onClose])

	// Handle successful booking.
	// Desktop: notify the parent and close (unchanged).
	// Mobile: show the "Booked!" confirmation screen and defer the callbacks.
	const handleBookSuccess = useCallback(() => {
		if (isMobile) {
			const fromRecurring = currentStepRef.current === 'recurring'
			const names = (appointments.length
				? appointments.map(a => a.pet?.name)
				: (recurringBase?.appointments || []).map(a => a.pet?.name)
			).filter(Boolean)
			setBookedInfo({
				names,
				recurring: fromRecurring,
				dateLabel: selectedDate ? dayjs(selectedDate).format('ddd, MMM D') : '',
				timeLabel: selectedTime ? dayjs(`2000-01-01 ${selectedTime}`).format('h:mm A') : '',
			})
			pendingCreatedRef.current = true
			return
		}
		onAppointmentCreated?.()
		onClose()
	}, [isMobile, appointments, recurringBase, selectedDate, selectedTime, onAppointmentCreated, onClose])

	// Dismiss the mobile confirmation (Done / X / scrim) — flush the deferred
	// created callback, then close. Safe for the normal (not-booked) path too.
	const handleDialogClose = useCallback(() => {
		setBookedInfo(null)
		if (pendingCreatedRef.current) {
			pendingCreatedRef.current = false
			onAppointmentCreated?.()
		}
		onClose()
	}, [onAppointmentCreated, onClose])

	// "Add another" on the confirmation — reset to a fresh step 1 (same as a
	// fresh open) while keeping pendingCreatedRef so the parent still gets
	// notified when the drawer finally closes.
	const handleAddAnother = useCallback(() => {
		setBookedInfo(null)
		setCurrentStep('single')
		setSelectedClient(prefilledAppointment?.client ?? prefilledClient)
		setSelectedDate(prefilledAppointment?.date ?? prefilledDateTime?.date ?? dayjs().format('YYYY-MM-DD'))
		setSelectedTime(prefilledAppointment?.time ?? prefilledDateTime?.time ?? '')
		setAppointments([])
		setEditingIndex(null)
		setRecurringBase(null)
		setRecurringOccurrences([])
	}, [prefilledAppointment, prefilledClient, prefilledDateTime])

	// Get pet IDs that are already added (for filtering in AddSingleAppointment)
	const existingPetIds = appointments
		.filter((_, i) => i !== editingIndex) // Exclude currently editing
		.map(apt => apt.pet?.id)
		.filter(Boolean)

	// Get the appointment being edited
	const editingAppointment = editingIndex !== null ? appointments[editingIndex] : null

	// Calculate total duration of appointments that come BEFORE the one being edited
	// For new appointments (editingIndex === null), include all existing appointments
	// For editing appointment at index N, only include appointments 0 to N-1
	const existingAppointmentsDuration = appointments
		.filter((_, i) => editingIndex === null ? true : i < editingIndex)
		.reduce((sum, apt) => {
			const serviceDuration = apt.service?.duration_minutes || 0
			const addOnsDuration = (apt.addOns || []).reduce((s, a) => s + (a.duration_minutes || 0), 0)
			return sum + serviceDuration + addOnsDuration
		}, 0)

	// Render content based on current step
	const renderContent = () => {
		if (currentStep === 'single') {
			// AI prefill flows into the first appointment only. Once the first
			// pet is saved (appointments.length > 0) further pets start clean.
			const isFirstAppt = appointments.length === 0 && editingIndex === null
			// If we previously stepped into the recurring flow and came back,
			// rehydrate the form from recurringBase so the merchant doesn't have
			// to re-pick pet/service/addons/employee. Falls back to the AI
			// prefill bundle when nothing was set up recurring yet.
			const preselectSource = recurringBase || prefilledAppointment
			return (
				<AddSingleAppointment
					merchant={merchant}
					isFirstPet={isFirstAppt}
					selectedClient={selectedClient}
					selectedDate={selectedDate}
					selectedTime={selectedTime}
					preselectedPet={isFirstAppt ? preselectSource?.pet ?? null : null}
					preselectedService={isFirstAppt ? preselectSource?.service ?? null : null}
					preselectedAddOns={isFirstAppt ? preselectSource?.addOns ?? null : null}
					preselectedEmployee={isFirstAppt ? preselectSource?.employee ?? null : null}
					// One employee per visit: pets after the first (adding or
					// editing) are locked to the first pet's employee. Editing
					// the first pet keeps the field open — that's where the
					// group's employee is changed.
					lockedEmployee={!isFirstAppt && editingIndex !== 0 ? appointments[0]?.employee ?? null : null}
					existingPetIds={existingPetIds}
					existingAppointmentsDuration={existingAppointmentsDuration}
					editingAppointment={editingAppointment}
					onSave={handleSingleSave}
					onCancel={handleSingleCancel}
					isMobile={isMobile}
					saveActionRef={saveActionRef}
				/>
			)
		}

		if (currentStep === 'recurring') {
			return (
				<RecurringAppointmentsOverview
					merchant={merchant}
					client={recurringBase?.client}
					appointments={recurringBase?.appointments || []}
					notes={recurringBase?.notes || ''}
					initialOccurrences={recurringOccurrences}
					onBack={handleRecurringBack}
					onBook={handleBookSuccess}
					isMobile={isMobile}
					saveActionRef={saveActionRef}
				/>
			)
		}

		return (
			<AppointmentsOverview
				merchant={merchant}
				client={selectedClient}
				date={selectedDate}
				time={selectedTime}
				appointments={appointments}
				onTimeChange={setSelectedTime}
				onDateChange={setSelectedDate}
				onAddPet={handleAddPet}
				onEditAppointment={handleEditAppointment}
				onDeleteAppointment={handleDeleteAppointment}
				onBook={handleBookSuccess}
				onCancel={handleDialogClose}
				onSetupRecurring={handleSetupRecurring}
				isMobile={isMobile}
				saveActionRef={saveActionRef}
				onAppointmentsChange={handleAppointmentsChange}
			/>
		)
	}

	// Mobile header — design "Mobile · add-appointment flow": title row with a
	// round close button (and a back arrow on the series-check step), then a
	// step subtitle with the two-pill progress indicator. Save/Book actions live
	// in each step's sticky footer, not up here.
	const renderMobileHeader = () => {
		const editingPetName = editingIndex !== null ? appointments[editingIndex]?.pet?.name : null
		const subtitle = currentStep === 'single'
			? (editingPetName
				? `Editing ${editingPetName}`
				: appointments.length ? 'Adding another pet' : 'Step 1 of 2 · details')
			: currentStep === 'overview'
				? 'Step 2 of 2 · review & book'
				: 'Series check · view & edit visits'

		return (
			// Top padding clears the device status bar / notch (safe-area inset)
			// now that the flow sits at the very top of the viewport.
			<Box sx={{ p: 'calc(env(safe-area-inset-top, 0px) + 20px) 16px 10px', fontFamily: L.font }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					{currentStep === 'recurring' && (
						<Box
							component="button"
							type="button"
							aria-label="Back to review"
							onClick={handleRecurringBack}
							sx={{
								width: 40, height: 40, flex: 'none', ml: '-10px', border: 'none',
								bgcolor: 'transparent', color: L.text, borderRadius: '11px',
								display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
								'&:active': { bgcolor: L.surface, color: L.primaryDeep },
							}}
						>
							<MsgIcon name="back" size={22} />
						</Box>
					)}
					<Typography component="h1" sx={{
						flex: 1, minWidth: 0, m: 0, fontSize: '23px', fontWeight: 800,
						letterSpacing: '-0.02em', color: L.text, fontFamily: L.font,
						whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
						display: 'flex', alignItems: 'center', gap: '8px',
					}}>
						{aiContext ? <>Create appointment <AiBadge /></> : 'Add appointment'}
					</Typography>
					<Box
						component="button"
						type="button"
						aria-label="Close"
						onClick={handleDialogClose}
						sx={{
							width: 36, height: 36, flex: 'none', borderRadius: '50%',
							border: `1.5px solid ${L.border}`, bgcolor: '#FFFFFF', color: L.muted,
							display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
							'&:active': { bgcolor: L.surface, color: L.text },
						}}
					>
						<Icon name="x" size={15} sw={2} />
					</Box>
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '3px' }}>
					<Box component="span" sx={{
						fontSize: '13px', fontWeight: 650, color: L.muted, minWidth: 0,
						whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
					}}>
						{subtitle}
					</Box>
					{currentStep !== 'recurring' && (
						<Box aria-hidden="true" sx={{ ml: 'auto', display: 'flex', gap: '4px', flex: 'none' }}>
							<Box sx={{ width: 24, height: 5, borderRadius: '99px', bgcolor: L.primary }} />
							<Box sx={{
								width: 24, height: 5, borderRadius: '99px',
								bgcolor: currentStep === 'overview' ? L.primary : L.border,
							}} />
						</Box>
					)}
				</Box>
			</Box>
		)
	}

	// Mobile "Booked!" confirmation (design: Mobile · key states → Booked).
	const renderMobileSuccess = () => (
		<Box sx={{
			flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
			justifyContent: 'center', gap: '8px', p: '40px 24px 70px', textAlign: 'center',
			fontFamily: L.font,
		}}>
			<Box sx={{
				width: 56, height: 56, borderRadius: '18px', bgcolor: L.primarySoft,
				color: L.primaryDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '4px',
			}}>
				<Icon name="paw" size={28} />
			</Box>
			<Typography component="h3" sx={{ m: 0, fontSize: '24px', fontWeight: 800, color: L.text, fontFamily: L.font }}>
				Booked!
			</Typography>
			<Typography sx={{ m: 0, fontSize: '14.5px', color: L.muted, fontWeight: 600, fontFamily: L.font }}>
				{[
					bookedInfo?.names?.join(' & '),
					[bookedInfo?.dateLabel, bookedInfo?.timeLabel ? `at ${bookedInfo.timeLabel}` : null]
						.filter(Boolean).join(' '),
				].filter(Boolean).join(' · ')}
			</Typography>
			{bookedInfo?.recurring && (
				<Typography sx={{ m: 0, fontSize: '13px', color: L.muted, fontWeight: 600, fontFamily: L.font }}>
					Recurring series added to the calendar
				</Typography>
			)}
			<Box sx={{ display: 'flex', gap: '10px', mt: '16px', width: '100%', maxWidth: 340 }}>
				<Box
					component="button"
					type="button"
					onClick={handleAddAnother}
					sx={{
						flex: 1, height: '52px', borderRadius: '14px',
						border: `1.5px solid ${L.border}`, bgcolor: '#FFFFFF', color: L.muted,
						fontSize: '15px', fontWeight: 650, fontFamily: L.font, cursor: 'pointer',
						'&:active': { color: L.text, borderColor: L.muted },
					}}
				>
					Add another
				</Box>
				<Box
					component="button"
					type="button"
					onClick={handleDialogClose}
					sx={{
						flex: 1, height: '52px', borderRadius: '14px', border: 'none',
						bgcolor: L.primary, color: L.onPrimary, fontSize: '15.5px', fontWeight: 750,
						fontFamily: L.font, cursor: 'pointer', boxShadow: `0 2px 10px ${L.primarySoft}`,
						'&:active': { bgcolor: L.primaryDeep },
					}}
				>
					Done
				</Box>
			</Box>
		</Box>
	)

	// AI note bubble (messaging AI flow only) — what was auto-filled / what's missing.
	const renderAiNote = () => aiContext?.note ? (
		<Box sx={{
			m: '0 16px 12px',
			p: '9px 11px',
			bgcolor: L.surface,
			borderRadius: '9px',
			fontSize: '11.5px',
			color: L.muted,
			fontWeight: 600,
			lineHeight: 1.45,
			fontFamily: L.font,
			flex: 'none'
		}}>
			{aiContext.note}
		</Box>
	) : null

	// Header for the messaging AI flow: sparkle icon + "Create appointment" +
	// AI badge + subtitle, per the Client Messaging design.
	const renderAiHeader = () => (
		<Box sx={{
			display: 'flex',
			alignItems: 'flex-start',
			gap: '11px',
			p: '16px 16px 13px',
			borderBottom: `1px solid ${L.border}`,
			mb: '12px'
		}}>
			<Box sx={{
				width: 32,
				height: 32,
				flex: 'none',
				borderRadius: '9px',
				bgcolor: '#FFFFFF',
				color: L.primaryDeep,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				boxShadow: '0 1px 4px rgba(95, 78, 194, 0.12)'
			}}>
				<MsgIcon name="auto" size={16} />
			</Box>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography sx={{
					fontFamily: L.font,
					fontWeight: 750,
					fontSize: '15.5px',
					color: L.text,
					display: 'flex',
					alignItems: 'center',
					gap: '7px'
				}}>
					Create appointment <AiBadge />
				</Typography>
				{aiContext?.subtitle && (
					<Typography sx={{
						fontFamily: L.font,
						fontSize: '11.5px',
						color: L.muted,
						fontWeight: 600,
						mt: '2px'
					}}>
						{aiContext.subtitle}
					</Typography>
				)}
			</Box>
		</Box>
	)

	// Desktop side panel content. In 'drawer' mode the caller owns the surface
	// (width, shadow, slide-in), so the panel just fills it.
	const asDrawer = desktopVariant === 'drawer'
	const renderDesktopPanel = () => (
		<Box sx={{
			// Fill the host surface: the calendar's inline wrapper (370px) or the
			// messaging AI dialog's Paper (400px, design .msg-aiform width).
			width: '100%',
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			bgcolor: '#FFFFFF',
			borderRadius: asDrawer ? 0 : '12px',
			boxShadow: asDrawer
				? 'none'
				: '0px 4px 4px -4px rgba(12, 12, 13, 0.05), 0px 16px 32px -4px rgba(12, 12, 13, 0.1)',
			overflow: 'hidden'
		}}>
			{/* Header */}
			{aiContext ? (
				<>
					{renderAiHeader()}
					{renderAiNote()}
				</>
			) : (
				<Box sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					alignSelf: 'stretch',
					gap: '27px',
					padding: '16px',
					borderBottom: currentStep === 'overview' ? 'none' : 'none'
				}}>
					<Typography sx={{
						fontFamily: L.font,
						fontWeight: 700,
						fontSize: '19px',
						letterSpacing: '-0.01em',
						color: L.text
					}}>
						Add appointment
					</Typography>
				</Box>
			)}

			{/* Content — no padding here: each step pads its own content (like the
			    mobile screen) so the scroll container reaches the panel edge and
			    the scrollbar/overlay thumb rides the outer edge, not 16px inset. */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				overflow: 'hidden'
			}}>
				{renderContent()}
			</Box>
		</Box>
	)

	// Mobile full-screen content (design: "Mobile · add-appointment flow" is an
	// exclusive full-screen takeover — no bottom sheet / drag handle).
	const renderMobileScreen = () => (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			height: '100%',
			bgcolor: L.bg,
			overflow: 'hidden',
			position: 'relative'
		}}>
			{bookedInfo ? renderMobileSuccess() : (
				<>
					{renderMobileHeader()}

					{renderAiNote()}

					{/* Content */}
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						flex: 1,
						overflow: 'hidden'
					}}>
						{renderContent()}
					</Box>
				</>
			)}
		</Box>
	)

	if (!open) return null

	// Mobile: full-screen takeover (the new design has no bottom drawer — the
	// flow owns the whole screen). We still use MUI Drawer for the portal,
	// backdrop-free overlay, focus trap, and slide-up transition, but sized to
	// the full viewport with square corners. LavMobileContext sizes every
	// lavender control (dropdowns, pickers, chips…) up for touch.
	if (isMobile) {
		return (
			<Drawer
				anchor="bottom"
				open={open}
				onClose={handleDialogClose}
				PaperProps={{
					sx: {
						height: '100%',
						maxHeight: '100%',
						borderRadius: 0,
						bgcolor: L.bg
					}
				}}
			>
				<LavMobileContext.Provider value={true}>
					{renderMobileScreen()}
				</LavMobileContext.Provider>
			</Drawer>
		)
	}

	// Desktop: Return the panel content (to be rendered beside calendar)
	return renderDesktopPanel()
})

export default AddAppointmentDialog

